// Login plan briefing. The agent chat shows this as its first message.
//
// Spec path: GET /brands/:id/briefing
// This app calls GET /api/brands/:brandId/briefing
//
// The briefing is stored data, not a model reply. Nothing in this module
// posts to /api/agent/chat. It stays off unless VITE_PLAN_BRIEFING=true.
// Account ids, tokens, feed keys, emails, and reviewer names are dropped.

import { apiJson } from '../auth/apiFetch';
import { formatMoney, formatStamp, isSecretText, safeText } from '../marketingPlans/client';

export type BriefingTest = {
  name: string;
  callLabel: string | null;
};

export type BriefingPlan = {
  title: string;
  revision: string | null;
  status: string | null;
  pacing: string | null;
  pacingDetail: string | null;
  tests: BriefingTest[];
  gaps: string[];
  approvals: string[];
  stock: string[];
};

export type PlanBriefing = {
  generatedLabel: string | null;
  unavailableReason: string | null;
  plans: BriefingPlan[];
  integrations: string[];
  nextAction: string | null;
};

type Rec = Record<string, unknown>;

export function planBriefingEnabled(env: { VITE_PLAN_BRIEFING?: string } = import.meta.env): boolean {
  return env.VITE_PLAN_BRIEFING === 'true';
}

export function briefingUrl(brandId: string): string {
  return `/api/brands/${encodeURIComponent(brandId)}/briefing`;
}

export async function getPlanBriefing(brandId: string): Promise<{ briefing: PlanBriefing | null }> {
  try {
    const body = await apiJson<unknown>(briefingUrl(brandId), { cache: 'no-store' });
    return { briefing: readPlanBriefing(body) };
  } catch {
    return { briefing: null };
  }
}

export function readPlanBriefing(body: unknown): PlanBriefing | null {
  const payload = payloadOf(body);
  if (!payload) return null;
  const currency = currencyCode(payload.currency);
  const fromPlans = records(payload.plans)
    .map((plan) => readPlan(plan, currency))
    .filter((plan): plan is BriefingPlan => plan != null && planHasContent(plan));
  const fromItems = fromPlans.length ? [] : readItemPlans(records(payload.items), currency);
  const plans = fromPlans.length ? fromPlans : fromItems;
  const integrations = [
    ...records(payload.integrations).map(integrationLine),
    ...records(payload.items).filter(isIntegrationItem).map(integrationLine)
  ].filter((line): line is string => Boolean(line));
  const briefing: PlanBriefing = {
    generatedLabel: generatedLabel(payload.generatedAt ?? payload.updatedAt),
    unavailableReason: prose(payload.unavailableReason, 400),
    plans,
    integrations: unique(integrations),
    nextAction: nextAction(payload.nextAction ?? payload.next ?? itemNext(records(payload.items)))
  };
  if (!briefing.unavailableReason && briefing.plans.length === 0 && briefing.integrations.length === 0 && !briefing.nextAction) {
    return null;
  }
  return briefing;
}

function readPlan(plan: Rec, currency: string | null): BriefingPlan | null {
  const title = prose(plan.title ?? plan.name ?? plan.plan, 160);
  if (!title) return null;
  const pacing = readPace(plan.pacing ?? plan.pace ?? plan.pacingStatus);
  return {
    title,
    revision: revisionText(plan.revision),
    status: prose(plan.status, 40),
    pacing,
    pacingDetail: pacingDetail(plan, currency),
    tests: readTests(plan.tests ?? plan.testCalls ?? plan.calls),
    gaps: stringList(plan.gaps ?? plan.coverageGaps),
    approvals: stringList(plan.approvals ?? plan.pendingApprovals).filter(isApproval),
    stock: stockLines(plan.stock ?? plan.stockFlags ?? plan.colorways)
  };
}

function readItemPlans(items: Rec[], currency: string | null): BriefingPlan[] {
  const buckets = new Map<string, BriefingPlan>();
  const bucketFor = (name: string): BriefingPlan => {
    const existing = buckets.get(name);
    if (existing) return existing;
    const created: BriefingPlan = {
      title: name, revision: null, status: null, pacing: null, pacingDetail: null,
      tests: [], gaps: [], approvals: [], stock: []
    };
    buckets.set(name, created);
    return created;
  };
  for (const item of items) {
    const kind = kindOf(item);
    if (!kind || kind === 'integration' || kind === 'next' || kind === 'next action') continue;
    if (kind === 'plan') {
      const title = prose(item.title ?? item.name ?? item.plan, 160);
      if (!title) continue;
      const bucket = bucketFor(title);
      bucket.revision = bucket.revision ?? revisionText(item.revision);
      bucket.status = bucket.status ?? prose(item.status, 40);
      continue;
    }
    const title = prose(item.plan ?? item.planTitle ?? item.title, 160) ?? 'Plans';
    const bucket = bucketFor(title);
    if (kind === 'pacing' || kind === 'pace') {
      bucket.pacing = bucket.pacing ?? readPace(item.pacing ?? item.pace ?? item.status ?? item.call);
      bucket.pacingDetail = bucket.pacingDetail ?? pacingDetail(item, currency);
    } else if (kind === 'test' || kind === 'call') {
      const test = readTest(item);
      if (test) bucket.tests.push(test);
    } else if (kind === 'gap') {
      const text = prose(item.text ?? item.summary ?? item.gap ?? item.label, 200);
      if (text) bucket.gaps.push(text);
    } else if (kind === 'approval') {
      const text = prose(item.text ?? item.summary ?? item.label, 160);
      if (text && isApproval(text)) bucket.approvals.push(text);
    } else if (kind === 'stock') {
      bucket.stock.push(...stockLines(item.stock ?? item));
    }
  }
  return [...buckets.values()].filter(planHasContent);
}

function planHasContent(plan: BriefingPlan): boolean {
  return Boolean(plan.status || plan.revision || plan.pacing || plan.pacingDetail
    || plan.tests.length || plan.gaps.length || plan.approvals.length || plan.stock.length);
}

function readTests(value: unknown): BriefingTest[] {
  return records(value).map(readTest).filter((test): test is BriefingTest => test != null);
}

function readTest(item: Rec): BriefingTest | null {
  const name = prose(item.name ?? item.title ?? item.label ?? item.test, 160);
  const callLabel = readCall(item.call ?? item.decision);
  if (!name && !callLabel) return null;
  return { name: name ?? 'Test', callLabel };
}

function pacingDetail(plan: Rec, currency: string | null): string | null {
  const nestedPace = isRec(plan.pacing) ? plan.pacing : null;
  const spent = splitText('Spend to date', plan.spendToDate ?? plan.spent ?? nestedPace?.spendToDate ?? nestedPace?.spent, currency);
  const weekly = splitText('weekly budget', plan.weekly ?? plan.weeklyBudget ?? nestedPace?.weekly ?? nestedPace?.weeklyBudget, currency);
  const flight = splitText('flight budget', plan.flight ?? plan.flightBudget ?? nestedPace?.flight ?? nestedPace?.flightBudget, currency);
  const parts = [spent, weekly, flight].filter((part): part is string => Boolean(part));
  return parts.length ? parts.join('. ') : null;
}

function splitText(label: string, value: unknown, currency: string | null): string | null {
  const parts = readParts(value);
  if (!parts) {
    const amount = num(value);
    if (amount == null) return null;
    return `${label} ${formatMoney(amount, currency)}`;
  }
  const bits = [
    parts.newSpend == null ? null : `new ${formatMoney(parts.newSpend, currency)}`,
    parts.existingSpend == null ? null : `existing ${formatMoney(parts.existingSpend, currency)}`
  ].filter((bit): bit is string => Boolean(bit));
  return bits.length ? `${label} ${bits.join(', ')}` : null;
}

function stockLines(value: unknown): string[] {
  if (typeof value === 'string') {
    const text = prose(value, 200);
    return text ? [text] : [];
  }
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return value.map((item) => prose(item, 200)).filter((item): item is string => Boolean(item));
  }
  return records(Array.isArray(value) ? value : [value]).map(stockLine).filter((line): line is string => Boolean(line));
}

function stockLine(row: Rec): string | null {
  const direct = prose(row.text ?? row.summary ?? row.flag, 200);
  if (direct && !('inStockSizes' in row) && !('colorway' in row) && !('name' in row)) return direct;
  const name = prose(row.colorway ?? row.name ?? row.label, 80);
  const sizes = num(row.inStockSizes ?? row.inStockSizeCount ?? row.sizesInStock);
  const ruleValue = num(row.sizeRule ?? row.minInStockSizes);
  const rule = ruleValue != null && ruleValue >= 1 ? ruleValue : 8;
  const below = row.belowStockRule === true || row.belowRule === true || row.lowStock === true || (sizes != null && sizes < rule);
  if (!below) return direct;
  if (!name) return direct ?? `Below the ${formatCount(rule)}-size in-stock rule`;
  return `${name} is below the ${formatCount(rule)}-size in-stock rule`;
}

function integrationLine(row: Rec): string | null {
  const provider = prose(row.provider ?? row.name ?? row.source, 60);
  const status = integrationStatus(row.status ?? row.health);
  const detail = prose(row.detail ?? row.note ?? row.summary ?? row.text, 160);
  const concerning = status === 'expiring' || status === 'expired' || status === 'missing'
    || (detail != null && /expir|missing|reconnect/i.test(detail));
  if (!concerning) return null;
  if (detail && provider && !detail.toLowerCase().includes(provider.toLowerCase())) return `${provider}: ${detail}`;
  if (detail) return detail;
  if (provider && status) return `${provider} is ${status}`;
  return null;
}

function isIntegrationItem(item: Rec): boolean {
  const kind = kindOf(item);
  return kind === 'integration' || kind === 'expiring';
}

function itemNext(items: Rec[]): unknown {
  const found = items.find((item) => {
    const kind = kindOf(item);
    return kind === 'next' || kind === 'next action';
  });
  return found ? (found.text ?? found.summary ?? found.label ?? found.action) : null;
}

function nextAction(value: unknown): string | null {
  if (isRec(value)) return nextAction(value.text ?? value.summary ?? value.label ?? value.action);
  const text = prose(value, 200);
  if (!text || /approved by|reviewer/i.test(text)) return null;
  return text;
}

function isApproval(text: string): boolean {
  return !/approved by|reviewer/i.test(text);
}

function readCall(value: unknown): string | null {
  const raw = prose(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'ahead' || key === 'ours ahead' || key === 'winner') return 'Ours ahead';
  if (key === 'too early' || key === 'early') return 'Too early';
  if (key === 'control ahead') return 'Control ahead';
  return null;
}

function readPace(value: unknown): string | null {
  if (isRec(value)) return readPace(value.status ?? value.pace ?? value.label ?? value.call);
  const raw = prose(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'ahead' || key === 'ahead of pace') return 'Ahead';
  if (key === 'behind' || key === 'behind pace') return 'Behind';
  if (key === 'on pace' || key === 'on track') return 'On pace';
  return null;
}

function integrationStatus(value: unknown): 'expiring' | 'expired' | 'missing' | null {
  const raw = prose(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'expiring' || key === 'expires soon') return 'expiring';
  if (key === 'expired') return 'expired';
  if (key === 'missing' || key === 'disconnected') return 'missing';
  return null;
}

function readParts(value: unknown): { newSpend: number | null; existingSpend: number | null } | null {
  if (!isRec(value)) return null;
  const hasNew = 'newSpend' in value || 'new' in value;
  const hasExisting = 'existingSpend' in value || 'existing' in value;
  if (!hasNew && !hasExisting) return null;
  return {
    newSpend: hasNew ? num(value.newSpend ?? value.new) : null,
    existingSpend: hasExisting ? num(value.existingSpend ?? value.existing) : null
  };
}

function stringList(value: unknown): string[] {
  if (typeof value === 'string') {
    const text = prose(value, 200);
    return text ? [text] : [];
  }
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    if (typeof item === 'string') return prose(item, 200);
    if (isRec(item)) return prose(item.text ?? item.summary ?? item.label ?? item.line, 200);
    return null;
  }).filter((item): item is string => Boolean(item));
}

function generatedLabel(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text || isSecretText(text)) return null;
  return formatStamp(text);
}

function payloadOf(body: unknown): Rec | null {
  if (!isRec(body)) return null;
  const nested = isRec(body.briefing) ? body.briefing : null;
  if (!nested) return body;
  const topHas = ['plans', 'items', 'integrations', 'nextAction', 'unavailableReason'].some((key) => body[key] != null);
  return topHas ? body : nested;
}

function kindOf(item: Rec): string | null {
  const raw = prose(item.kind ?? item.type, 60);
  return raw ? normalizeKind(raw) : null;
}

function normalizeKind(raw: string): string {
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'plan' || key === 'plan status') return 'plan';
  if (key === 'test' || key === 'call' || key === 'test call') return 'test';
  if (key === 'gap' || key === 'creative gap' || key === 'coverage gap') return 'gap';
  if (key === 'pacing' || key === 'pace' || key === 'pacing status') return 'pacing';
  if (key === 'approval' || key === 'pending approval') return 'approval';
  if (key === 'stock' || key === 'stock flag') return 'stock';
  if (key === 'integration' || key === 'expiring' || key === 'integration expiring' || key === 'expiring integration') return 'integration';
  if (key === 'next' || key === 'next action') return 'next';
  return key;
}

function prose(value: unknown, limit: number): string | null {
  const text = safeText(value, limit);
  if (!text || looksLikePii(text)) return null;
  return text;
}

function looksLikePii(text: string): boolean {
  if (/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/.test(text)) return true;
  if (/\b\d{3}[-.\s]\d{3}[-.\s]\d{4}\b/.test(text)) return true;
  return false;
}

function revisionText(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(Math.trunc(value));
  return prose(value, 40);
}

function currencyCode(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!/^[A-Za-z]{3}$/.test(text)) return null;
  return text.toUpperCase();
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function isRec(value: unknown): value is Rec {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function records(value: unknown): Rec[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRec);
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}
